import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PoweredBy from '../PoweredBy';

interface RequirementCardProps {
  image: string;
  title: string;
  description: string;
  isAccepted: boolean;
  onAccept: () => void;
}

const RequirementCard: React.FC<RequirementCardProps> = ({ 
  image, 
  title, 
  description, 
  isAccepted, 
  onAccept 
}) => (
  <motion.div
    className="relative rounded-2xl overflow-hidden cursor-pointer group"
    style={{ 
      backgroundColor: 'rgba(255,255,255,0.05)', 
      border: isAccepted ? '2px solid #D31334' : '1px solid rgba(255,255,255,0.1)' 
    }}
    onClick={onAccept}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    {/* Image */}
    <div className="h-48 overflow-hidden">
      <img 
        src={image} 
        alt={title}
        className="w-full h-full object-cover transition-transform group-hover:scale-105"
      />
    </div>
    
    {/* Content */}
    <div className="p-6">
      <h3 className="text-white text-xl font-medium mb-2">{title}</h3>
      <p className="text-white/60 text-sm">{description}</p>
    </div>

    {/* Checkmark overlay */}
    <AnimatePresence>
      {isAccepted && (
        <motion.div
          className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#D31334' }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const LululemonBeforeStartView: React.FC = () => {
  const navigate = useNavigate();
  const [acceptedItems, setAcceptedItems] = useState<Set<string>>(new Set());

  const requirements = [
    {
      id: 'lululemon-only',
      image: '/assets/images/lululemon/like-new-items.jpg',
      title: 'lululemon Brand Only',
      description: 'We only accept lululemon-branded items. Check for the logo on the item or rip tag.'
    },
    {
      id: 'clean-condition',
      image: '/assets/images/lululemon/clean-gear.jpg',
      title: 'Clean & Wearable',
      description: 'Items should be freshly laundered, free of stains, tears, and excessive pilling.'
    },
    {
      id: 'no-underwear',
      image: '/assets/images/lululemon/accepted-items.jpg',
      title: 'Outerwear & Accessories',
      description: 'We accept leggings, shorts, tops, jackets, and accessories. No underwear or swimwear.'
    }
  ];

  const handleItemAccept = (itemId: string) => {
    setAcceptedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const allItemsAccepted = requirements.every(req => acceptedItems.has(req.id));

  // Auto-navigate when all items are accepted
  useEffect(() => {
    if (allItemsAccepted) {
      const timer = setTimeout(() => {
        navigate('/lululemon/scan');
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [allItemsAccepted, navigate]);

  const handleBack = () => {
    navigate('/lululemon/takeback');
  };

  return (
    <motion.div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: '#1A1A1A' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          background: 'radial-gradient(ellipse at 50% 30%, #D31334 0%, transparent 70%)'
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex justify-center mx-20 mt-10">
          <div className="w-full max-w-[1200px] flex justify-between items-center">
            <img 
              src="/assets/images/logos/lululemon-white.png" 
              alt="lululemon" 
              className="h-8"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <span className="text-white/60 text-sm uppercase tracking-wider">Like New</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center px-20 py-12">
          {/* Heading */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <h1 
              className="text-5xl font-light mb-4"
              style={{ color: '#FFFFFF', fontFamily: "'Calibre', 'Helvetica Neue', sans-serif" }}
            >
              Before You Start
            </h1>
            <p 
              className="text-xl font-light"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Tap each card to confirm your items meet our requirements
            </p>
          </motion.div>

          {/* Requirements Grid */}
          <motion.div
            className="grid grid-cols-3 gap-8 max-w-5xl w-full mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {requirements.map((req, index) => (
              <motion.div
                key={req.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
              >
                <RequirementCard
                  image={req.image}
                  title={req.title}
                  description={req.description}
                  isAccepted={acceptedItems.has(req.id)}
                  onAccept={() => handleItemAccept(req.id)}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Progress indicator */}
          <motion.div
            className="flex gap-3 mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {requirements.map((req) => (
              <div
                key={req.id}
                className="w-3 h-3 rounded-full transition-all"
                style={{
                  backgroundColor: acceptedItems.has(req.id) ? '#D31334' : 'rgba(255,255,255,0.2)'
                }}
              />
            ))}
          </motion.div>

          {/* Back button */}
          <motion.button
            onClick={handleBack}
            className="text-white/60 hover:text-white text-sm uppercase tracking-wider transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Back
          </motion.button>
        </div>

        {/* Footer */}
        <div className="flex justify-center pb-8">
          <PoweredBy />
        </div>
      </div>
    </motion.div>
  );
};

export default LululemonBeforeStartView;

